import {
  CreateBankAccountInput,
  GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery,
  UpdateBankAccountInput
} from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createBankAccountSchema,
  createRequiredSensitiveBankAccountDataSchema,
  TransferType,
  updateBankAccountSchema,
  updateRequiredSensitiveBankAccountSchema
} from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { AddressComponent } from '../templateComponents/rowComponents/addressComponent';
import { StyledHeaderComponent, TwoColumnWrapper } from '../templateComponents/styles';
import { IRenderTemplateRow } from '../templateComponents/types';
import { CREATE_BANK_ACCOUNT_MUTATION, UPDATE_BANK_ACCOUNT_MUTATION } from './queries';

const transferTypeSelectOptions: ISelectValue[] = [
  { label: 'Select', value: null },
  createSimpleSelectOption(getEnumKeyByValue(TransferType, TransferType.ABA), TransferType.ABA),
  createSimpleSelectOption(getEnumKeyByValue(TransferType, TransferType.CHIPS), TransferType.CHIPS)
];

const getTransferCodeFieldLabel = (transferType: keyof typeof TransferType) => {
  const transferCodeFieldLabels = {
    [TransferType.ABA]: 'ABA/Routing number',
    [TransferType.CHIPS]: 'CHIPS Number'
  };

  return transferCodeFieldLabels[transferType] || 'Money Transfer Code';
};

interface EnterBankAccountInfoModalPageProps {
  row: IRenderTemplateRow;
  pageName?: string;
  currentItemId: string | null;
  tenantId: string;
  entityId: string;
  bankAccounts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'];
  mode: 'documentationTab' | 'wizard';
  ignoreRequired?: boolean;
  getBankAccountIds: (name: string) => string | string[];
  saveBankAccountIds: (name: string, ids: string | string[]) => void;
  bankAccountsAndContactsRefetch: () => void;
  closeModalPage: () => void;
}

export const EnterBankAccountInfoModalPage = ({
  row,
  pageName = '',
  currentItemId,
  tenantId,
  entityId,
  bankAccounts,
  mode,
  ignoreRequired = false,
  getBankAccountIds,
  saveBankAccountIds,
  bankAccountsAndContactsRefetch,
  closeModalPage
}: EnterBankAccountInfoModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [zodSchema, setZodSchema] = useState(null);

  const methods = useForm<GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'][number]>({
    mode: 'onChange',
    resolver: zodSchema ? zodResolver(zodSchema) : null
  });

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createBankAccount] = useMutation(CREATE_BANK_ACCOUNT_MUTATION);
  const [updateBankAccount] = useMutation(UPDATE_BANK_ACCOUNT_MUTATION);

  useEffect(() => {
    switch (mode) {
      case 'documentationTab':
        if (isEditMode) {
          setZodSchema(updateBankAccountSchema);
          return;
        }
        setZodSchema(createBankAccountSchema);
        break;
      case 'wizard':
        if (isEditMode) {
          setZodSchema(updateRequiredSensitiveBankAccountSchema);
          return;
        }
        setZodSchema(createRequiredSensitiveBankAccountDataSchema);
        break;
      default:
        break;
    }
  }, [isEditMode, mode]);

  useEffect(() => {
    if (currentItemId) {
      const currentBankAccount = bankAccounts.find((bankAccount) => bankAccount.id === currentItemId);
      // Bank account is not linked to a legal entity, so it is a template bank account
      if (!currentBankAccount.legalEntityId) {
        const { id, ...templateBankAccount } = currentBankAccount;
        // Remove the "Create from Template: " string from the name
        const name = templateBankAccount.name.replace('Create from Template: ', '');
        methods.reset((defaultValues) => ({ ...defaultValues, ...templateBankAccount, name, legalEntityId: entityId, tenantId }));
        return;
      }
      methods.reset((defaultValues) => ({ ...defaultValues, ...currentBankAccount, legalEntityId: entityId, tenantId }));
      setIsEditMode(true);
      return;
    }
    methods.reset((defaultValues) => ({ ...defaultValues, legalEntityId: entityId, tenantId }));
  }, [currentItemId, tenantId]);

  const onSave = async (data: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'][number]) => {
    try {
      setErrorMessage(null);
      setIsSubmitting(true);

      if (isEditMode) {
        const { legalEntityId, ...dataToUpdate } = data;

        const { data: updateBankAccountData } = await updateBankAccount({
          variables: {
            input: {
              id: currentItemId,
              ...dataToUpdate
            } as UpdateBankAccountInput
          }
        });

        const { id } = updateBankAccountData.updateBankAccount;
        const bankAccountIds = getBankAccountIds(`${pageName}.${row.name}`);
        if (row?.config?.selectType === 'multiple') {
          saveBankAccountIds(`${pageName}.${row.name}`, (bankAccountIds || []).includes(id) ? bankAccountIds : [...(bankAccountIds || []), id]);
        } else {
          saveBankAccountIds(`${pageName}.${row.name}`, id);
        }
        bankAccountsAndContactsRefetch();
        closeModalPage();
        return;
      }

      if (!isEditMode) {
        const { data: createBankAccountData } = await createBankAccount({
          variables: {
            input: data as CreateBankAccountInput
          }
        });

        const { id } = createBankAccountData.createBankAccount;
        const bankAccountIds = getBankAccountIds(`${pageName}.${row.name}`);
        saveBankAccountIds(`${pageName}.${row.name}`, row?.config?.selectType === 'multiple' ? [...(bankAccountIds || []), id] : id);
        bankAccountsAndContactsRefetch();
        closeModalPage();
      }
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving Bank Account Info" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={closeModalPage} />} />
        <PageTitle title="Enter Bank Account Info" />
      </MainWrap>
      {errorMessage && (
        <Banner
          icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
          title="Error"
          description={errorMessage}
          bgColor={themeVar('context.error')}
        />
      )}
      <MainWrap>
        <PaddingWrap>
          <FormProvider {...methods}>
            <BankAccountInfoForm>
              <BankAccountInfoWrapper isMobile={isMobile}>
                <RowWrapper>
                  <StyledHeaderComponent>Beneficiary Bank Details</StyledHeaderComponent>
                  <TwoColumnWrapper isMobile={isMobile}>
                    <Controller
                      name="data.transferType"
                      control={methods.control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Select
                          label="Money Transfer Type"
                          value={value || ''}
                          options={transferTypeSelectOptions}
                          setValue={onChange}
                          errorText={error?.message}
                          size="md"
                          isRequiredField={!ignoreRequired}
                        />
                      )}
                    />
                  </TwoColumnWrapper>
                  {!!methods.watch('data.transferType') && (
                    <TwoColumnWrapper isMobile={isMobile}>
                      <Controller
                        name="data.transferCode"
                        control={methods.control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            label={getTransferCodeFieldLabel(methods.watch('data.transferType'))}
                            value={value || ''}
                            onChange={(e) => {
                              onChange(e.target.value);
                            }}
                            errorText={error?.message}
                            size="md"
                            isRequiredField={!ignoreRequired}
                          />
                        )}
                      />
                    </TwoColumnWrapper>
                  )}
                  <TwoColumnWrapper isMobile={isMobile}>
                    <Controller
                      name="name"
                      control={methods.control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Beneficiary Bank Name"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          size="md"
                          isRequiredField={!ignoreRequired}
                        />
                      )}
                    />
                  </TwoColumnWrapper>
                </RowWrapper>
                <RowWrapper>
                  <StyledHeaderComponent>Beneficiary Bank Address</StyledHeaderComponent>
                  <AddressComponent
                    row={{
                      name: 'data.address',
                      component: 'address',
                      config: {
                        ...row.config,
                        labels: {
                          streetAddress: 'Bank Street Address'
                        }
                      },
                      validation: {
                        required: true
                      }
                    }}
                    ignoreRequired={ignoreRequired}
                  />
                </RowWrapper>
                <RowWrapper>
                  <StyledHeaderComponent>Beneficiary Account Details</StyledHeaderComponent>
                  <TwoColumnWrapper isMobile={isMobile}>
                    <Controller
                      name="data.accountName"
                      control={methods.control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Beneficiary Account Name"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          isRequiredField={!ignoreRequired}
                          autoComplete="off"
                          size="md"
                        />
                      )}
                    />
                    <Controller
                      name="data.accountNumber"
                      control={methods.control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="Beneficiary Account Number"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          isRequiredField={!ignoreRequired}
                          size="md"
                        />
                      )}
                    />
                  </TwoColumnWrapper>
                  <TwoColumnWrapper isMobile={isMobile}>
                    <Controller
                      name="data.furtherCredit"
                      control={methods.control}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <Input
                          label="For Further Credit"
                          value={value || ''}
                          onChange={(e) => {
                            onChange(e.target.value);
                          }}
                          errorText={error?.message}
                          size="md"
                        />
                      )}
                    />
                  </TwoColumnWrapper>
                </RowWrapper>
              </BankAccountInfoWrapper>
              <FormFooter
                onCancel={closeModalPage}
                onSave={methods.handleSubmit(onSave, (error) => {
                  console.log(error);
                })}
                disableSaveButton={(methods.formState.isSubmitted && !methods.formState.isValid) || isSubmitting}
                showRequiredFieldsWarning={false}
              />
            </BankAccountInfoForm>
          </FormProvider>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const BankAccountInfoForm = styled.form`
  margin-top: 50px;
`;

const BankAccountInfoWrapper = styled.div<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '20px' : '40px')};
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${themeVar('layer.1')};
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '30px' : '40px')};
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;
